import { z } from "zod";

/**
 * Wire schemas for `GET /users` / `GET /documents` (`aic.service.admin`,
 * ISSUE-326/327 — producer-owned, not yet built; contract sample: ISSUE-329
 * §Cross-service JSON contract). Zod 4.5.4 (verified installed 2026-09-02,
 * `npm view zod version`).
 *
 * Deliberately **non-strict** (ADR-016): `z.object()` drops an unrecognised
 * key at parse instead of rejecting the whole response or passing it through.
 * That is also what makes the field-discipline boundary (ADR-052 §5) hold
 * even against a future producer regression that leaks a privacy-sensitive
 * field the response contract omits by design (see ISSUE-326's own boundary
 * line) — this parse step strips it before it ever reaches a view, on top of
 * the repo-wide check in `fieldDiscipline.test.ts`. Stay non-strict until the
 * producer's shape is confirmed live; a `.strict()` schema would start
 * rejecting the whole response the day the real API adds an unrelated field.
 */

/** Shared by both resources — a row belongs to exactly one channel. */
const ChannelSchema = z.enum(["mobile", "telegram"]);
export type Channel = z.infer<typeof ChannelSchema>;

/**
 * `GET /users` (ISSUE-455 Deliverable 3, ADR-052 §3 amended 2026-09-18):
 * a single distinct-count of `mobile#{sub}` values in the document registry,
 * not a roster. No array, no `userRef`, no channel field, no identifier of
 * any kind — Telegram is out of scope on this surface. Empty registry →
 * `{ "count": 0 }`.
 */
export const UsersResponseSchema = z.object({
  count: z.number().int().nonnegative(),
});
export type UsersResponse = z.infer<typeof UsersResponseSchema>;

export const DocumentSchema = z.object({
  documentId: z.string().min(1),
  channel: ChannelSchema,
  /** The opaque `tenantRef` — an identifier, not content (ADR-052 §5). */
  ownerRef: z.string().min(1),
  // Left as a free string on purpose: the consultant-sessions state machine
  // (ADR-035) has more states than ADR-052 has enumerated for this payload,
  // and the producer has not shipped yet — an enum here would start rejecting
  // valid responses the day the real API sends a status this schema didn't
  // anticipate.
  status: z.string().min(1),
  sizeBytes: z.number().int().nonnegative(),
  // ISSUE-464 / ADR-016 §1: the producer's actual wire output is offset-
  // suffixed, not Z-suffixed — verified live 2026-09-19, e.g.
  // "2026-08-18T10:27:49.011186+00:00" (upstream Python `datetime.isoformat()`
  // in aic.agent.consultant.py). `{ offset: true }` widens the accepted
  // *valid* shape to match the producer; it still rejects a genuinely
  // malformed or zone-less timestamp (schemas.test.ts regression guard).
  startedAt: z.iso.datetime({ offset: true }),
  updatedAt: z.iso.datetime({ offset: true }),
});
export type Document = z.infer<typeof DocumentSchema>;

export const DocumentsResponseSchema = z.object({
  documents: z.array(DocumentSchema),
});
export type DocumentsResponse = z.infer<typeof DocumentsResponseSchema>;
